import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useLocale } from "@/i18n/locale";
import { GA_MEASUREMENT_ID, initAnalytics, setAnalyticsConsent } from "./analytics";
import {
  clearAnalyticsCookies,
  hasDoNotTrack,
  readConsent,
  writeConsent,
  type Consent,
} from "./consent";

type ConsentContextValue = {
  consent: Consent | null;
  /** Whether the banner should be on screen. */
  isAsking: boolean;
  accept: () => void;
  decline: () => void;
  /** Reopen the banner so a decision can be changed. */
  reopen: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const [consent, setConsent] = useState<Consent | null>(() =>
    // A Do Not Track signal is an answer, so treat it as a decline rather than
    // asking again.
    hasDoNotTrack() ? "denied" : readConsent(),
  );
  const [reopened, setReopened] = useState(false);

  // Only ask when there is no answer — or when the footer explicitly reopens it.
  const isAsking = reopened || (consent === null && !hasDoNotTrack());

  // Consent Mode v2: the tag loads for everyone (so Google can detect it and so
  // a later grant applies immediately), but starts with analytics_storage
  // denied. Consent controls STORAGE, not whether the script exists.
  //
  // initAnalytics is idempotent; the consent update runs on every change, which
  // is what carries a returning visitor's stored decision into gtag.
  useEffect(() => {
    initAnalytics(locale);
    setAnalyticsConsent(consent === "granted");
  }, [consent, locale]);

  const accept = useCallback(() => {
    writeConsent("granted");
    setConsent("granted");
    setReopened(false);
  }, []);

  const decline = useCallback(() => {
    writeConsent("denied");
    setConsent("denied");
    setReopened(false);
    // If they had accepted earlier the cookies already exist. Declining has to
    // remove them, not merely stop collecting.
    clearAnalyticsCookies(GA_MEASUREMENT_ID);
  }, []);

  const reopen = useCallback(() => setReopened(true), []);

  const value = useMemo<ConsentContextValue>(
    () => ({ consent, isAsking, accept, decline, reopen }),
    [consent, isAsking, accept, decline, reopen],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used inside <ConsentProvider>");
  return ctx;
}
